import { FC, useMemo } from 'react'
import { Form, Select, Space, Spin, Typography } from 'antd'
import styled from 'styled-components'
import { ContractorSelect } from '../../../components/form-items/contractor/contractor'
import type { ShipmentPeriodStatsPayload } from '../../../types/api/shipment-period-stats'
import formatIntegerBySpace from '../../../helpers/formatters/format-integer-by-space'

const Row = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: flex-start;
    gap: 12px 20px;
    width: 100%;
    text-align: left;
`

const MONTH_LABELS = [
    'Январь',
    'Февраль',
    'Март',
    'Апрель',
    'Май',
    'Июнь',
    'Июль',
    'Август',
    'Сентябрь',
    'Октябрь',
    'Ноябрь',
    'Декабрь',
]
const MONTH_OPTIONS = Array.from({ length: 12 }, (_, i) => ({
    value: i + 1,
    label: MONTH_LABELS[i],
}))

function yearSelectOptions(currentYear?: number): { value: number; label: string }[] {
    const year = currentYear ?? new Date().getFullYear()
    const yNow = new Date().getFullYear()
    const from = Math.min(2018, year, yNow)
    const to = Math.max(2030, year, yNow + 1)
    const out: { value: number; label: string }[] = []
    for (let y = from; y <= to; y++) {
        out.push({ value: y, label: String(y) })
    }
    return out
}

interface PeriodStatAmountProps {
    label: string
    value: number | undefined
    isFetching: boolean
    isError: boolean
}

const PeriodStatAmount: FC<PeriodStatAmountProps> = ({ label, value, isFetching, isError }) => (
    <Space size={8}>
        <Typography.Text type={'secondary'}>{label}</Typography.Text>
        {isError ? (
            <Typography.Text type={'danger'}>—</Typography.Text>
        ) : isFetching ? (
            <Spin size={'small'} />
        ) : value != null ? (
            <Typography.Text strong>{formatIntegerBySpace(Math.round(value))}</Typography.Text>
        ) : null}
    </Space>
)

interface ShipmentsPeriodStatsRowProps {
    stats: ShipmentPeriodStatsPayload | undefined
    isFetching: boolean
    isError: boolean
}

export const ShipmentsPeriodStatsRow: FC<ShipmentsPeriodStatsRowProps> = ({
    stats,
    isFetching,
    isError,
}) => {
    const form = Form.useFormInstance()
    const year = Form.useWatch('year', form)
    const yearOptions = useMemo(() => yearSelectOptions(year), [year])

    return (
        <Row>
            <Space size={4} wrap={false}>
                <Form.Item name="month" noStyle>
                    <Select
                        size={'small'}
                        popupMatchSelectWidth={false}
                        dropdownStyle={{ minWidth: 'fit-content' }}
                        options={MONTH_OPTIONS}
                    />
                </Form.Item>
                <Form.Item name="year" noStyle>
                    <Select size={'small'} options={yearOptions} />
                </Form.Item>
                <Form.Item name="client" noStyle>
                    <ContractorSelect size={'small'} />
                </Form.Item>
            </Space>

            {!isFetching && stats ? (
                <Typography.Text type={'secondary'}>Отгрузок: {stats.shipmentCount}</Typography.Text>
            ) : null}

            <PeriodStatAmount
                label="Прибыль за месяц:"
                value={stats?.totalProfit}
                isFetching={isFetching}
                isError={isError}
            />
            <PeriodStatAmount
                label="Оборот за месяц:"
                value={stats?.totalTurnover}
                isFetching={isFetching}
                isError={isError}
            />

            {isError ? (
                <Typography.Text type={'danger'}>Не удалось загрузить итоги</Typography.Text>
            ) : null}
        </Row>
    )
}
